import type { ReactElement } from 'react';
import Skeleton from '@mui/material/Skeleton';

export const SKELETON_BASE_CLASS = 'search-results-skeleton';

type BoneProps = {
  className?: string;
  height: number;
  width?: number | string;
  variant?: 'text' | 'rectangular' | 'rounded' | 'circular';
};

const Bone = ({
  className,
  height,
  width = '100%',
  variant = 'rounded',
}: BoneProps) => (
  <Skeleton
    className={className}
    variant={variant}
    height={height}
    width={width}
    animation="wave"
  />
);

/** Mirrors `search-result-card` layout so loading tiles match final card size. */
const GlobalSearchResultSkeleton = () => (
  <article
    className="search-result-card search-result-card--skeleton"
    aria-hidden
  >
    <div className="search-result-card__image-container">
      <div className={`${SKELETON_BASE_CLASS}__image-wrap`}>
        <Skeleton
          className={`${SKELETON_BASE_CLASS}__image`}
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
        />
      </div>
    </div>
    <div className="search-result-card__content">
      <Bone className={`${SKELETON_BASE_CLASS}__title`} height={22} width="92%" />
      <Bone className={`${SKELETON_BASE_CLASS}__meta`} height={16} width="55%" />
      <Bone className={`${SKELETON_BASE_CLASS}__date`} height={14} width="40%" />
      <Bone className={`${SKELETON_BASE_CLASS}__link`} height={18} width="38%" />
    </div>
  </article>
);

type Props = {
  count?: number;
  listClassName?: string;
};

const SKELETON_VISIBLE_COUNT = 8;

const SearchResultsSkeleton = ({
  count = SKELETON_VISIBLE_COUNT,
  listClassName = 'search-results',
}: Props): ReactElement => {
  const n = Math.min(Math.max(1, count), SKELETON_VISIBLE_COUNT);

  return (
    <ul
      className={`${listClassName} ${listClassName}--loading`}
      aria-hidden
    >
      {Array.from({ length: n }, (_, i) => (
        <li key={i} className={`${listClassName}__item`}>
          <GlobalSearchResultSkeleton />
        </li>
      ))}
    </ul>
  );
};

export default SearchResultsSkeleton;
